import React, { FunctionComponent, useState } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RichText, DateField, Text } from '@sitecore-jss/sitecore-jss-react';
import CTAButton, { ctaEnums } from '../@shared/ctaButton';
import ModalDialog from '../@shared/modalDialog';
import Picture from '../@shared/picture';
import RoutableSitecoreLink from '../@shared/routableSitecoreLink';
import { ContentHubSpecialOffersSlideModel } from './models/specialOffers.model';
import { googleClickCtaEvent } from '../../utils/analytics';
import { appendRootToRichTextInternalLinks } from '../../utils/appendRootToRichTextInternalLinks.utils';
import { appendRootToInternalLink } from '../../utils/appendRootToInternalLink.utils';
import useFormatDate from '../../hooks/useFormatDate';

const CarouselSlide: FunctionComponent<
  WithTranslation & { model: ContentHubSpecialOffersSlideModel; isDesktop: boolean; cardIndex: number }
> = ({ model, isDesktop, cardIndex, t }) => {
  const [modalIsOpen, setIsOpen] = useState(false);
  const formattedDate = useFormatDate(model?.expiration?.value);
  const { buttonPrimary, buttonSecondary } = model || {};
  const secondaryLinkWithRootAppended = appendRootToInternalLink(buttonSecondary?.value?.href);

  if (buttonSecondary?.value?.href) {
    buttonSecondary.value.href = secondaryLinkWithRootAppended;
  }

  const openModal = (): void => {
    setIsOpen(true);
  };

  const closeModal = (): void => {
    setIsOpen(false);
  };

  return (
    <>
      {model.disclaimer && (
        <ModalDialog isOpen={modalIsOpen} close={closeModal} className="special-offers-modal">
          <RichText tag="div" className={'rich-text'} field={{ value: `**${appendRootToRichTextInternalLinks(model.disclaimer)}` }} />
        </ModalDialog>
      )}
      {model.badge ? (
        <div className="card-label">
          <Text field={{ value: model.badge }} tag="p" />
        </div>
      ) : null}
      <div className={`special-offers-card special-offers-card--${model.hasWideCardStyle ? 'large' : 'small'}`}>
        <div className="card-with-header-image slide-card slide-card--strech">
          <div className="special-offers-card-wrapper">
            {model?.asset?.rendition_desktop_size1000?.url ? (
              <Picture
                productImageProps={{
                  desktopRendition: model.desktopRendition,
                  mobileRendition: model.mobileRendition,
                  pictureClassName: 'slide-card__image',
                }}
              />
            ) : (
              <div className="slide-card__image"></div>
            )}

            <div className="special-offers-card__text-button-wrapper">
              <div className="special-offers-card__text-wrapper">
                <div className="special-offers-card__title">
                  {model.title && (
                    <RichText
                      tag="span"
                      field={model.title}
                      className="slide-card__text slide-card__text--inline slide-card__text--overflow-unset rich-text"
                    />
                  )}
                </div>
                {model.shortDescription && (
                  <RichText
                    editable={false}
                    tag="div"
                    field={model.shortDescription}
                    className="special-offers-listing-card__text special-offers-listing-card__text--shortDescription rich-text"
                  />
                )}
                {model.disclaimer && (
                  <RichText onClick={openModal} field={{ value: t('cnhi-disclaimer') }} className="special-offers-card__text rich-text" />
                )}
                <div className="slide-card__text slide-card__text--strech">
                  <RichText tag="div" field={model.expirationLabel} className={'rich-text'} /> :&nbsp;
                  <DateField field={model.expiration} tag="p" render={() => formattedDate} />
                </div>
              </div>
              <div className="slide-card__button-wrapper">
                {buttonPrimary?.value?.href && buttonPrimary?.value?.title && (
                  <CTAButton type={ctaEnums.primaryRed}>
                    <RoutableSitecoreLink
                      className="slide-card__button slide-card__button--primary"
                      field={buttonPrimary}
                      onClick={(): void => googleClickCtaEvent('Button', buttonPrimary?.value.title, model?.title?.value, cardIndex)}
                    >
                      {buttonPrimary.value.title}
                    </RoutableSitecoreLink>
                  </CTAButton>
                )}
                {model?.secondaryButtonExistsOrIsEE && (!isDesktop || model.hasWideCardStyle) && (
                  <CTAButton type={ctaEnums.primaryWhite}>
                    <RoutableSitecoreLink
                      className="slide-card__button slide-card__button--secondary"
                      field={buttonSecondary}
                      onClick={(): void => googleClickCtaEvent('Button', buttonSecondary?.value.title, model?.title?.value, cardIndex)}
                    >
                      {buttonSecondary.value.title}
                    </RoutableSitecoreLink>
                  </CTAButton>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withTranslation()(CarouselSlide);
